import { 
  format, 
  parseISO, 
  isValid, 
  addDays, 
  addMonths, 
  addYears, 
  differenceInDays, 
  differenceInMonths, 
  differenceInYears,
  startOfDay,
  endOfDay,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isSameYear
} from 'date-fns';

export class DateUtility {
  /**
   * Format a date string or Date object
   */
  static format(date: string | Date, formatStr: string = 'MM/dd/yyyy'): string {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      if (!isValid(dateObj)) {
        return '';
      }
      return format(dateObj, formatStr);
    } catch (error) {
      console.error('Date formatting error:', error);
      return '';
    }
  }

  /**
   * Parse a date string
   */
  static parse(dateString: string): Date | null {
    try {
      const date = parseISO(dateString);
      return isValid(date) ? date : null;
    } catch (error) {
      console.error('Date parsing error:', error);
      return null;
    }
  }

  /**
   * Check if a date is valid
   */
  static isValid(date: string | Date): boolean {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      return isValid(dateObj);
    } catch (error) {
      return false;
    }
  }

  /**
   * Add days to a date
   */
  static addDays(date: string | Date, days: number): Date | null {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      if (!isValid(dateObj)) return null;
      return addDays(dateObj, days);
    } catch (error) {
      console.error('Add days error:', error);
      return null;
    }
  }

  /**
   * Add months to a date
   */
  static addMonths(date: string | Date, months: number): Date | null {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      if (!isValid(dateObj)) return null;
      return addMonths(dateObj, months);
    } catch (error) {
      console.error('Add months error:', error);
      return null;
    }
  }

  /**
   * Add years to a date
   */
  static addYears(date: string | Date, years: number): Date | null {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      if (!isValid(dateObj)) return null;
      return addYears(dateObj, years);
    } catch (error) {
      console.error('Add years error:', error);
      return null;
    }
  }

  /**
   * Get difference in days between two dates
   */
  static differenceInDays(dateLeft: string | Date, dateRight: string | Date): number {
    try {
      const left = typeof dateLeft === 'string' ? parseISO(dateLeft) : dateLeft;
      const right = typeof dateRight === 'string' ? parseISO(dateRight) : dateRight;
      
      if (!isValid(left) || !isValid(right)) return 0;
      return differenceInDays(left, right);
    } catch (error) {
      console.error('Difference in days error:', error);
      return 0;
    }
  }

  /**
   * Get difference in months between two dates
   */
  static differenceInMonths(dateLeft: string | Date, dateRight: string | Date): number {
    try {
      const left = typeof dateLeft === 'string' ? parseISO(dateLeft) : dateLeft;
      const right = typeof dateRight === 'string' ? parseISO(dateRight) : dateRight;
      
      if (!isValid(left) || !isValid(right)) return 0;
      return differenceInMonths(left, right);
    } catch (error) {
      console.error('Difference in months error:', error);
      return 0;
    }
  }

  /**
   * Get difference in years between two dates
   */
  static differenceInYears(dateLeft: string | Date, dateRight: string | Date): number {
    try {
      const left = typeof dateLeft === 'string' ? parseISO(dateLeft) : dateLeft;
      const right = typeof dateRight === 'string' ? parseISO(dateRight) : dateRight;
      
      if (!isValid(left) || !isValid(right)) return 0;
      return differenceInYears(left, right);
    } catch (error) {
      console.error('Difference in years error:', error);
      return 0;
    }
  }

  /**
   * Get start of day
   */
  static startOfDay(date: string | Date): Date | null {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      if (!isValid(dateObj)) return null;
      return startOfDay(dateObj);
    } catch (error) {
      console.error('Start of day error:', error);
      return null;
    }
  }

  /**
   * Get end of day
   */
  static endOfDay(date: string | Date): Date | null {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      if (!isValid(dateObj)) return null;
      return endOfDay(dateObj);
    } catch (error) {
      console.error('End of day error:', error);
      return null;
    }
  }

  /**
   * Check if date is after another date
   */
  static isAfter(date: string | Date, dateToCompare: string | Date): boolean {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      const compareObj = typeof dateToCompare === 'string' ? parseISO(dateToCompare) : dateToCompare;
      
      if (!isValid(dateObj) || !isValid(compareObj)) return false;
      return isAfter(dateObj, compareObj);
    } catch (error) {
      console.error('Is after error:', error);
      return false;
    }
  }

  /**
   * Check if date is before another date
   */
  static isBefore(date: string | Date, dateToCompare: string | Date): boolean {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      const compareObj = typeof dateToCompare === 'string' ? parseISO(dateToCompare) : dateToCompare;
      
      if (!isValid(dateObj) || !isValid(compareObj)) return false;
      return isBefore(dateObj, compareObj);
    } catch (error) {
      console.error('Is before error:', error);
      return false;
    }
  }

  /**
   * Check if two dates are the same day
   */
  static isSameDay(date: string | Date, dateToCompare: string | Date): boolean {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      const compareObj = typeof dateToCompare === 'string' ? parseISO(dateToCompare) : dateToCompare;
      
      if (!isValid(dateObj) || !isValid(compareObj)) return false;
      return isSameDay(dateObj, compareObj);
    } catch (error) {
      console.error('Is same day error:', error);
      return false;
    }
  }

  /**
   * Check if two dates are the same month
   */
  static isSameMonth(date: string | Date, dateToCompare: string | Date): boolean {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      const compareObj = typeof dateToCompare === 'string' ? parseISO(dateToCompare) : dateToCompare;
      
      if (!isValid(dateObj) || !isValid(compareObj)) return false;
      return isSameMonth(dateObj, compareObj);
    } catch (error) {
      console.error('Is same month error:', error);
      return false;
    }
  }

  /**
   * Check if two dates are the same year
   */
  static isSameYear(date: string | Date, dateToCompare: string | Date): boolean {
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      const compareObj = typeof dateToCompare === 'string' ? parseISO(dateToCompare) : dateToCompare;
      
      if (!isValid(dateObj) || !isValid(compareObj)) return false;
      return isSameYear(dateObj, compareObj);
    } catch (error) {
      console.error('Is same year error:', error);
      return false;
    }
  }

  /**
   * Get current date as ISO string
   */
  static now(): string {
    return new Date().toISOString();
  }

  /**
   * Get current date as Date object
   */
  static today(): Date {
    return new Date();
  }
}
